import React, { useState, useEffect, useCallback } from 'react';
import RequestThread from '../../components/RequestThread';
import axios from 'axios';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import PlanSelect from '../../components/PlanSelect';
import PhoneInput from '../../components/PhoneInput';
import { usePartnerUnread } from '../../context/PartnerUnreadContext';

const STATUS_META = {
  pending:  { cls: 'badge-gray',  icon: '⏳' },
  approved: { cls: 'badge-green', icon: '✓'  },
  rejected: { cls: 'badge-red',   icon: '✗'  },
};

// ── New / Edit Request Modal ──────────────────────────────
function RequestModal({ restaurants, editRequest, onClose, onSuccess, t }) {
  const isEditing = !!editRequest; // true when editing an existing pending request
  const [type, setType] = useState(editRequest?.type || 'create');
  const [form, setForm] = useState({
    name:            editRequest?.requestedData?.name    || editRequest?.editData?.name    || '',
    phoneNumber:     editRequest?.requestedData?.phoneNumber || editRequest?.editData?.phoneNumber || '',
    address:         editRequest?.requestedData?.address || editRequest?.editData?.address || '',
    planId:          editRequest?.requestedData?.planId  || editRequest?.editData?.planId  || '',
    restaurantId:    editRequest?.restaurantId?._id      || editRequest?.restaurantId      || '',
    initialMessage:  '',
  });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const selectedRest = restaurants.find(r => r._id === form.restaurantId);

  const handle = async (e) => {
    e.preventDefault(); setError('');
    setLoading(true);
    try {
      const payload = { type, initialMessage: form.initialMessage };
      if (type === 'create') {
        payload.requestedData = { name: form.name, phoneNumber: form.phoneNumber, address: form.address, planId: form.planId };
      } else {
        payload.restaurantId = form.restaurantId;
        if (type === 'edit') payload.editData = { name: form.name, phoneNumber: form.phoneNumber, address: form.address, planId: form.planId };
      }

      if (isEditing) {
        // Update existing pending request — delete old and resubmit
        await axios.delete(`/api/requests/${editRequest._id}`);
      }
      await axios.post('/api/requests', payload);
      onSuccess();
    } catch (err) { setError(err.response?.data?.message || 'Error'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <h2 style={{ fontSize: 22 }}>{isEditing ? t('editRequest') : t('newRequest')}</h2>
          <button onClick={onClose} style={{ background: 'none', color: 'var(--text-muted)', fontSize: 22, lineHeight: 1 }}>×</button>
        </div>

        {/* Type toggle — only for new requests */}
        {!isEditing && (
          <div className="toggle-pill" style={{ marginBottom: 20 }}>
            <button className={type === 'create'     ? 'active' : ''} onClick={() => setType('create')}>{t('createRestaurant')}</button>
            <button className={type === 'edit'       ? 'active' : ''} onClick={() => setType('edit')}>{t('editRestaurant')}</button>
            <button className={type === 'deactivate' ? 'active' : ''} onClick={() => setType('deactivate')}>🚫 {t('deactivate')}</button>
          </div>
        )}

        <form onSubmit={handle}>
          {/* Restaurant picker for edit / deactivate */}
          {(type === 'edit' || type === 'deactivate') && (
            <div className="form-group">
              <label className="form-label">{t('restaurant')}</label>
              <select required value={form.restaurantId} onChange={e => set('restaurantId', e.target.value)}>
                <option value="">{t('selectOption')}</option>
                {restaurants.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>
          )}

          {/* Deactivate warning */}
          {type === 'deactivate' && selectedRest && (
            <div style={{ background: 'var(--red-bg)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: 'var(--radius-sm)', padding: '12px 14px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, color: 'var(--red)', fontWeight: 500 }}>
                ⚠️ {t('deactivateWarning').replace('{name}', selectedRest.name)}
              </div>
            </div>
          )}

          {/* Create / Edit fields */}
          {(type === 'create' || type === 'edit') && (
            <>
              <div className="form-group">
                <label className="form-label">{t('restaurantName')} {type === 'create' && '*'}</label>
                <input type="text" required={type === 'create'} value={form.name} onChange={e => set('name', e.target.value)} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">{t('phoneNumber')}</label>
                  <PhoneInput value={form.phoneNumber} onChange={v => set('phoneNumber', v)} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('plan')}</label>
                  <PlanSelect value={form.planId} onChange={(id) => set('planId', id)} showPrice={false} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">{t('address')}</label>
                <input type="text" value={form.address} onChange={e => set('address', e.target.value)} />
              </div>
            </>
          )}

          <div className="form-group" style={{ marginBottom: 20 }}>
            <label className="form-label">{t('initialMessage')}</label>
            <textarea rows={2} placeholder={t('initialMessagePlaceholder')}
              value={form.initialMessage} onChange={e => set('initialMessage', e.target.value)} style={{ resize: 'none' }} />
          </div>

          {error && (
            <div style={{ background: 'var(--red-bg)', color: 'var(--red)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: 13, marginBottom: 14, border: '1px solid rgba(224,92,92,0.2)' }}>
              {error}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} className="btn-ghost" style={{ flex: 1 }}>{t('cancel')}</button>
            <button type="submit" className="btn-primary" style={{ flex: 2 }}
              disabled={loading || (type === 'deactivate' && !form.restaurantId)}>
              {loading ? t('saving') : t('submitRequest')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────
export default function PartnerRequests() {
  const { t }     = useSettings();
  const { admin } = useAuth();
  const { decrementUnread } = usePartnerUnread();

  const [requests,    setRequests]    = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [showModal,   setShowModal]   = useState(false);   // new request
  const [editingReq,  setEditingReq]  = useState(null);    // editing existing pending request
  const [expanded,    setExpanded]    = useState(null);

  const fetchAll = useCallback(async () => {
    try {
      const [rqRes, restRes] = await Promise.all([
        axios.get('/api/requests'),
        axios.get('/api/partner/restaurants'),
      ]);
      setRequests(rqRes.data);
      setRestaurants(restRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleExpand = async (req) => {
    const opening = expanded !== req._id;
    setExpanded(opening ? req._id : null);
    if (opening && req.partnerUnread > 0) {
      decrementUnread(req.partnerUnread);
      setRequests(prev => prev.map(r => r._id === req._id ? { ...r, partnerUnread: 0 } : r));
      axios.post(`/api/requests/${req._id}/read`).catch(() => {});
    }
  };

  const handleCancel = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm(t('cancelRequest') + '?')) return;
    try { await axios.delete(`/api/requests/${id}`); fetchAll(); }
    catch (err) { alert(err.response?.data?.message || 'Error'); }
  };

  const handleEdit = (req, e) => {
    e.stopPropagation();
    setEditingReq(req);
  };

  const typeLabel = (type) => ({
    create:     t('createRestaurant'),
    edit:       t('editRestaurant'),
    deactivate: `🚫 ${t('deactivate')}`,
  }[type] || type);

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 style={{ fontSize: 34, marginBottom: 6 }}>{t('myRequests')}</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {requests.filter(r => r.status === 'pending').length} {t('pending').toLowerCase()} · {requests.length} total
          </p>
        </div>
        {/* New request button — only opens new request modal */}
        <button className="btn-primary" onClick={() => setShowModal(true)}>+ {t('newRequest')}</button>
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>{t('loading')}</div>
      ) : requests.length === 0 ? (
        <div className="card" style={{ padding: 60, textAlign: 'center' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ color: 'var(--text-muted)' }}>{t('noRequests')}</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {requests.map(req => {
            const meta   = STATUS_META[req.status] || STATUS_META.pending;
            const isOpen = expanded === req._id;
            const isPending = req.status === 'pending';

            return (
              <div key={req._id} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', cursor: 'pointer' }}
                  onClick={() => handleExpand(req)}>

                  {/* Left: title + badges */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
                      <span style={{ fontWeight: 500, fontSize: 14 }}>
                        {typeLabel(req.type)}
                        {req.requestedData?.name  ? `: ${req.requestedData.name}`  : ''}
                        {req.restaurantId?.name   ? `: ${req.restaurantId.name}`   : ''}
                      </span>
                      <span className={`badge ${meta.cls}`}>{meta.icon} {t(req.status)}</span>
                      {req.partnerUnread > 0 && (
                        <span style={{ background: 'var(--red)', color: '#fff', borderRadius: 10, fontSize: 10, fontWeight: 700, padding: '1px 6px' }}>
                          {req.partnerUnread} {t('newMessages')}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
                      {new Date(req.createdAt).toLocaleDateString('en-GB')} · {new Date(req.createdAt).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                      {req.messages?.length > 0 && ` · ${req.messages.length} msg`}
                    </div>
                  </div>

                  {/* Right: action buttons in the highlighted area */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                    {isPending && (
                      <>
                        {/* Edit button */}
                        <button
                          className="btn-ghost"
                          style={{ fontSize: 12, padding: '6px 11px' }}
                          onClick={e => handleEdit(req, e)}
                          title={t('editRequest')}
                        >
                          ✎ {t('editAdmin')}
                        </button>
                        {/* Cancel / Delete button */}
                        <button
                          className="btn-danger"
                          style={{ fontSize: 12 }}
                          onClick={e => handleCancel(req._id, e)}
                        >
                          {t('cancelRequest')}
                        </button>
                      </>
                    )}
                    {/* Expand toggle — always visible, stops propagation so buttons don't also expand */}
                    <div
                      style={{ color: 'var(--text-dim)', fontSize: 16, padding: '0 4px', cursor: 'pointer' }}
                      onClick={() => handleExpand(req)}
                    >
                      {isOpen ? '▲' : '▼'}
                    </div>
                  </div>
                </div>

                {/* Expanded: request data + thread */}
                {isOpen && (
                  <div style={{ borderTop: '1px solid var(--border)', padding: '16px 20px', background: 'var(--surface2)' }}>
                    {(req.type === 'create' || req.type === 'edit') && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 10, marginBottom: 16 }}>
                        {Object.entries(req.type === 'create' ? (req.requestedData || {}) : (req.editData || {}))
                          .filter(([, v]) => v && typeof v === 'string')
                          .map(([k, v]) => (
                            <div key={k}>
                              <div style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 2 }}>{k}</div>
                              <div style={{ fontSize: 13, fontWeight: 500 }}>{v}</div>
                            </div>
                          ))}
                      </div>
                    )}
                    <RequestThread request={req} currentUser={admin} onMessageSent={fetchAll} t={t} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* New request modal */}
      {showModal && (
        <RequestModal
          restaurants={restaurants}
          onClose={() => setShowModal(false)}
          onSuccess={() => { setShowModal(false); fetchAll(); }}
          t={t}
        />
      )}

      {/* Edit existing pending request modal */}
      {editingReq && (
        <RequestModal
          restaurants={restaurants}
          editRequest={editingReq}
          onClose={() => setEditingReq(null)}
          onSuccess={() => { setEditingReq(null); fetchAll(); }}
          t={t}
        />
      )}
    </div>
  );
}
